// Help Slip — the paper form the team fills in BEFORE asking to meet the MD about a
// challenge. Migration 187; backend src/modules/helpslip/helpslip.routes.js.
//
// ALL THREE SOLUTIONS ARE MANDATORY (ruling 1) and the backend refuses a partial slip — the
// UI here says so up front and names the blank field, rather than letting someone type for
// five minutes and then be rejected.
//
// VISIBILITY (ruling 2): the person and admin. This file simply does not render what the
// server will not return; the rule itself is enforced server-side on every route.
//
// PRINTING (ruling: "bring a hard copy") uses @media print CSS, the same approach as the
// huddle sheet and for the same reasons — it paginates properly, it costs no font bundling,
// and the identical stylesheet gives the phone layout for free.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { askReason } from '../utils/reportError';

const C = { navy: '#3B3A8F', red: '#E22D2D', green: '#166534', amber: '#B45309',
  text: '#1a1a2e', muted: '#888', border: '#e8e8e8', bg: '#F7F6F3' };

// The four printed fields, in the printed order, worded as the paper form words them.
const L = {
  challenge: 'CHALLENGE FACED / QUESTION',
  s1: 'BEST SOLUTION',
  s2: 'SECOND BEST SOLUTION',
  s3: 'THIRD BEST SOLUTION',
};
const CHOSEN_LABEL = { first: 'Best Solution', second: 'Second Best Solution', third: 'Third Best Solution', none: 'None of the three' };

const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const btn = { padding: '8px 15px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const btnP = { ...btn, background: C.navy, color: '#fff' };

// MODULE SCOPE, DELIBERATELY. Declared inside a component's render body these get a NEW
// function identity on every render, so React sees a different element type, unmounts the
// subtree and rebuilds the DOM node — and the caret, selection and scroll position go with
// it. Merchant 1 lost focus after every keystroke in the solution boxes for exactly this.
// Same defect as HuddleGrid's Tray. They take what they used to close over as props.
const SlipRow = ({ label, rows, value, onChange }) => (
  <div style={{ borderTop: '1px solid #333' }}>
    <div style={{ background: C.bg, padding: '6px 12px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>{label}</div>
    <div style={{ padding: 10 }}>
      <textarea style={{ ...input, minHeight: rows || 54, resize: 'vertical' }} value={value} onChange={onChange} />
    </div>
  </div>
);

const SlipField = ({ label, children, min }) => (
  <div style={{ borderTop: '1.5px solid #000' }}>
    <div style={{ padding: '5px 11px', fontSize: 10.5, fontWeight: 700, borderBottom: '1px solid #999', letterSpacing: 0.3 }}>{label}</div>
    <div style={{ padding: 11, fontSize: 13, minHeight: min || 54, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{children}</div>
  </div>
);
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const PRINT_CSS = `
@media screen { .hs-print { max-width: 800px; margin: 0 auto; } }
@media print {
  @page { size: A4 portrait; margin: 14mm; }
  /* The app shell reserves a full viewport (sidebar height:100vh). visibility:hidden hides
     its ink but keeps its BOX, which paginates as trailing blank pages — collapse it. */
  html, body { height: auto !important; overflow: visible !important; }
  aside { display: none !important; }
  [style*="100vh"] { height: auto !important; min-height: 0 !important; }
  body * { visibility: hidden; }
  .hs-print, .hs-print * { visibility: visible; }
  /* static, not absolute: out-of-flow content does not paginate. */
  .hs-print { position: static !important; width: 100% !important; }
  .hs-noprint { display: none !important; }
  .hs-box { border: 1.5px solid #000 !important; page-break-inside: avoid; }
}
@media (max-width: 560px) {
  .hs-head { grid-template-columns: 1fr !important; }
  .hs-head > div { border-right: none !important; border-bottom: 1px solid #000; }
  .hs-head > div:last-child { border-bottom: none; }
}
`;

export default function HelpSlip() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // The MD view is admin AND management (Rajan, 2026-08-21) — a deliberate widening of the
  // module's ruling 2, mirrored byte-for-byte by authorize('admin','management') on the route.
  const canSeeAll = ['admin', 'management'].includes(user?.role);
  const [tab, setTab] = useState('mine');
  // Pending count for the tab badge. Fetched by the PARENT so the number is on the tab before
  // anyone opens it — the badge exists to say how many people are waiting without a click.
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!canSeeAll) return;
    api.get('/help-slips/all').then(r => setCounts(r.data.counts || null)).catch(() => {});
  }, [canSeeAll]);
  return (
    <div style={{ padding: 20, maxWidth: 1080, margin: '0 auto', color: C.text }}>
      <style>{PRINT_CSS}</style>
      <div className="hs-noprint" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>🙋 Help Slip</h1>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>
          Fill this in before asking to meet the MD. Bring the printed slip to the meeting.
          {/* This line used to promise "only you and the MD can see it". Management can now read
              every slip, and leaving the old wording up would be telling people something untrue
              about who reads what they write. */}
          {' '}Visible to you, the MD and management.
        </div>
      </div>
      <div className="hs-noprint" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['mine', '📄 My Slips'], ['new', '+ New Slip'],
          ...(isAdmin ? [['queue', '📥 Queue']] : []),
          ...(canSeeAll ? [['all', `📋 Help Slips${counts ? ' · ' + counts.pending : ''}`]] : []),
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...btn, border: '1px solid ' + (tab === k ? C.navy : '#ddd'),
            background: tab === k ? C.navy : '#fff', color: tab === k ? '#fff' : '#666' }}>{l}</button>
        ))}
      </div>
      {tab === 'new' && <NewSlip onDone={() => setTab('mine')} />}
      {tab === 'mine' && <MySlips isAdmin={isAdmin} />}
      {tab === 'queue' && isAdmin && <Queue />}
      {tab === 'all' && canSeeAll && <AllSlips onCounts={setCounts} />}
    </div>
  );
}

// ─── THE MD VIEW — every slip, oldest first, READ ONLY ──────────────────────
// No respond control and no approve button: a slip is answered in the meeting, not on the
// screen. The whole slip is on the row, so nothing has to be opened to be read in a meeting —
// challenge, all three solutions, and for an answered slip what was actually decided.
function AllSlips({ onCounts }) {
  const [filter, setFilter] = useState('pending');
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get('/help-slips/all?filter=' + filter)
      .then(r => { setRows(r.data.data || []); setCounts(r.data.counts || null); onCounts && onCounts(r.data.counts || null); })
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const TABS = [['pending', 'Pending'], ['answered', 'Answered'], ['all', 'All']];
  // Lateness bands. A slip that has waited two months is not the same object as one raised
  // yesterday, and the row should say so before anybody reads the text.
  const age = (d) => (d >= 30 ? { fg: '#A32D2D', bg: '#FCEBEB' } : d >= 7 ? { fg: C.amber, bg: '#FEF3E2' } : { fg: C.muted, bg: '#F3F4F6' });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ ...btn, padding: '5px 12px', fontSize: 12,
            border: '1px solid ' + (filter === k ? C.navy : '#ddd'),
            background: filter === k ? '#EEF0FA' : '#fff', color: filter === k ? C.navy : '#666', fontWeight: filter === k ? 700 : 500 }}>
            {l}{counts ? ' · ' + counts[k] : ''}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: C.muted }}>Oldest first · answered in the meeting, not here</span>
      </div>

      {rows === null && <div style={{ padding: 24, color: C.muted, fontSize: 12 }}>Loading…</div>}
      {rows && !rows.length && (
        <div style={{ padding: 28, textAlign: 'center', color: C.muted, border: '1px dashed ' + C.border, borderRadius: 8, fontSize: 13 }}>
          {filter === 'pending' ? 'Nothing waiting.' : filter === 'answered' ? 'Nothing answered yet.' : 'No slips raised yet.'}
        </div>
      )}

      {(rows || []).map(s => {
        const [lbl, col, bg] = STATUS[s.status] || STATUS.pending;
        const a = age(s.days_open || 0);
        const answered = s.status === 'met' || s.status === 'resolved';
        return (
          <div key={s.id} style={{ border: '1px solid ' + C.border, borderLeft: '3px solid ' + a.fg, borderRadius: 9, padding: 14, marginBottom: 10, background: '#fff' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontFamily: 'monospace', color: C.navy }}>{s.slip_no}</strong>
              <span style={{ fontSize: 11, fontWeight: 700, color: col, background: bg, borderRadius: 10, padding: '2px 9px' }}>{lbl}</span>
              <span style={{ fontSize: 12 }}><strong>{s.raised_by_name}</strong>
                <span style={{ color: C.muted }}> · {s.department || s.raised_by_role}</span></span>
              {s.gpo_no && <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.red }}>
                GPO {s.gpo_no}{s.style_no ? ' · ' + s.style_no : ''}{s.colour ? ' · ' + s.colour : ''}</span>}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: C.muted }}>{fmtD(s.created_at)}</span>
                <span title={answered ? 'Days from raising to the answer' : 'Days open so far'}
                  style={{ fontSize: 11, fontWeight: 700, color: a.fg, background: a.bg, borderRadius: 10, padding: '2px 9px' }}>
                  {s.days_open}d {answered ? 'to answer' : 'open'}
                </span>
              </span>
            </div>

            <div style={{ marginTop: 9, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{s.challenge}</div>

            <div style={{ marginTop: 9, display: 'grid', gap: 5 }}>
              {[s.solution_1, s.solution_2, s.solution_3].map((sol, i) => (
                <div key={i} style={{ fontSize: 12, color: '#444', display: 'flex', gap: 7, lineHeight: 1.45,
                  ...(answered && s.chosen_solution === ['first', 'second', 'third'][i]
                    ? { background: '#EAF7EE', borderRadius: 5, padding: '3px 7px', fontWeight: 600 } : {}) }}>
                  <span style={{ color: C.muted, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                  <span>{sol}</span>
                  {answered && s.chosen_solution === ['first', 'second', 'third'][i] &&
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: C.green, fontWeight: 700, flexShrink: 0 }}>✓ CHOSEN</span>}
                </div>
              ))}
            </div>

            {answered && (
              <div style={{ marginTop: 10, paddingTop: 9, borderTop: '1px dashed ' + C.border }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Outcome</div>
                <div style={{ fontSize: 12.5, color: C.text, marginTop: 3, lineHeight: 1.5 }}>{s.response}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  {/* 'none' is a real answer — all three were read and none taken — and reads
                      differently from a slip nobody has answered yet. */}
                  {s.chosen_solution === 'none' ? 'None of the three solutions was taken' : 'Took the ' + s.chosen_solution + ' solution'}
                  {s.responded_by_name ? ' · ' + s.responded_by_name : ''}
                  {s.responded_at ? ' · ' + fmtD(s.responded_at) : ''}
                </div>
              </div>
            )}

            {s.status === 'withdrawn' && (
              <div style={{ marginTop: 10, paddingTop: 9, borderTop: '1px dashed ' + C.border, fontSize: 11.5, color: C.muted }}>
                {/* Withdrawn is neither pending nor answered, so it surfaces only under All —
                    counting it as answered would overstate how much actually got dealt with. */}
                <strong>Withdrawn by the person who raised it.</strong> {s.withdraw_reason}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── GPO picker — optional (ruling: not every challenge is order-related) ────
function GpoPick({ value, onChange }) {
  const [q, setQ] = useState(value || '');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || q.trim().length < 1) { setHits([]); return; }
    let dead = false;
    const t = setTimeout(async () => {
      const r = await api.get('/help-slips/gpo-search?q=' + encodeURIComponent(q.trim())).catch(() => null);
      if (!dead && r) setHits(r.data.data || []);
    }, 200);
    return () => { dead = true; clearTimeout(t); };
  }, [q, open]);
  return (
    <div style={{ position: 'relative' }}>
      <input style={{ ...input, fontFamily: 'monospace' }} placeholder="GPO no. (optional)" value={q}
        maxLength={20} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
        onChange={e => { setQ(e.target.value); setOpen(true); onChange(e.target.value.trim() || null); }} />
      {open && q.trim() && (
        <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #ddd', borderRadius: 7, boxShadow: '0 6px 20px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto' }}>
          {hits.length ? hits.map(g => (
            <div key={g.gpo_no} onMouseDown={() => { setQ(g.gpo_no); onChange(g.gpo_no); setOpen(false); }}
              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5' }}>
              <strong style={{ fontFamily: 'monospace', color: C.red }}>{g.gpo_no}</strong>
              <span style={{ color: C.muted }}> · {[g.style_no, g.colour].filter(Boolean).join(' · ') || g.item_desc || '—'}</span>
            </div>
          )) : <div style={{ padding: '9px 11px', fontSize: 12, color: C.muted }}>No GPO matches “{q.trim()}”. Leave blank if the challenge is not about an order.</div>}
        </div>
      )}
    </div>
  );
}

// ─── NEW SLIP ────────────────────────────────────────────────────────────────
function NewSlip({ onDone }) {
  const [f, setF] = useState({ person_name: '', phone: '', department: '', challenge: '', solution_1: '', solution_2: '', solution_3: '', gpo_no: null });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Ruling 5 — header prefilled from the user record, and editable.
  useEffect(() => { api.get('/help-slips/prefill').then(r => setF(x => ({ ...x, ...r.data.data }))).catch(() => {}); }, []);

  const blanks = ['solution_1', 'solution_2', 'solution_3'].filter(k => !String(f[k] || '').trim());
  const ready = String(f.challenge || '').trim() && blanks.length === 0;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { await api.post('/help-slips', f); onDone(); }
    catch (ex) { setErr(ex.response?.data?.error || 'Could not raise the slip'); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit}>
      <div style={{ background: '#FFFBF2', border: '1px solid #F0D9A8', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.55 }}>
        <strong>All three solutions are required.</strong> The slip exists so a challenge arrives with three
        answers already thought through, not just the problem. A slip with a blank solution will be refused.
      </div>

      <div className="hs-box" style={{ border: '1.5px solid #333', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <div className="hs-head" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
          {[['Name', 'person_name'], ['Ph. No.', 'phone'], ['Department', 'department']].map(([lbl, k], i) => (
            <div key={k} style={{ padding: '8px 12px', borderRight: i < 2 ? '1px solid #333' : 'none' }}>
              <div style={{ fontSize: 9, letterSpacing: 1, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{lbl}</div>
              <input style={{ ...input, border: 'none', padding: 0, fontSize: 14, fontWeight: 700 }}
                value={f[k] || ''} onChange={e => setF({ ...f, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <SlipRow label={L.challenge} rows={70} value={f.challenge || ''} onChange={e => setF({ ...f, challenge: e.target.value })} />
        <SlipRow label={L.s1} value={f.solution_1 || ''} onChange={e => setF({ ...f, solution_1: e.target.value })} />
        <SlipRow label={L.s2} value={f.solution_2 || ''} onChange={e => setF({ ...f, solution_2: e.target.value })} />
        <SlipRow label={L.s3} value={f.solution_3 || ''} onChange={e => setF({ ...f, solution_3: e.target.value })} />
        <div style={{ borderTop: '1px solid #333', padding: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>RELATED GPO (optional)</span>
          <div style={{ width: 220 }}><GpoPick value={f.gpo_no} onChange={v => setF({ ...f, gpo_no: v })} /></div>
          <span style={{ fontSize: 11, color: C.muted }}>Leave blank if the challenge is not about an order.</span>
        </div>
      </div>

      {err && <div style={{ color: C.red, marginTop: 10, fontSize: 12.5 }}>{err}</div>}
      {!ready && blanks.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: C.amber }}>
          ⚠ Still blank: {blanks.map(b => ({ solution_1: 'Best Solution', solution_2: 'Second Best Solution', solution_3: 'Third Best Solution' }[b])).join(', ')}
        </div>
      )}
      <button type="submit" disabled={busy || !ready}
        style={{ ...btnP, marginTop: 14, padding: '10px 22px', opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }}>
        {busy ? 'Raising…' : 'Raise this slip'}
      </button>
    </form>
  );
}

// ─── MY SLIPS ────────────────────────────────────────────────────────────────
function MySlips({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    api.get('/help-slips' + (isAdmin ? '?mine=1' : '')).then(r => setRows(r.data.data || [])).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  if (open) return <SlipView slip={open} onBack={() => { setOpen(null); load(); }} canRespond={false} />;
  return (
    <div>
      {!rows.length && <div style={{ padding: 28, textAlign: 'center', color: C.muted, border: '1px dashed ' + C.border, borderRadius: 8 }}>
        No help slips yet. Raise one when you have a challenge — with three solutions ready.</div>}
      {rows.map(s => <SlipCard key={s.id} s={s} onOpen={() => setOpen(s)} />)}
    </div>
  );
}

// ─── RAJAN'S QUEUE — open slips, oldest first ───────────────────────────────
function Queue() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get('/help-slips/queue').then(r => setRows(r.data.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (open) return <SlipView slip={open} onBack={() => { setOpen(null); load(); }} canRespond />;
  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        Open slips, oldest first — {rows.length} waiting.
      </div>
      {!rows.length && <div style={{ padding: 28, textAlign: 'center', color: C.muted, border: '1px dashed ' + C.border, borderRadius: 8 }}>
        Nothing waiting.</div>}
      {rows.map(s => <SlipCard key={s.id} s={s} onOpen={() => setOpen(s)} showWho />)}
    </div>
  );
}

const STATUS = {
  pending: ['⏳ Pending', C.amber, '#FEF3E2'],
  met: ['🤝 Met', C.navy, '#EEF0FA'],
  resolved: ['✓ Resolved', C.green, '#EAF7EE'],
  withdrawn: ['⃠ Withdrawn', C.muted, '#F3F4F6'],
};
function SlipCard({ s, onOpen, showWho }) {
  const [lbl, col, bg] = STATUS[s.status] || STATUS.pending;
  return (
    <div onClick={onOpen} style={{ border: '1px solid ' + C.border, borderRadius: 9, padding: 14, marginBottom: 10, cursor: 'pointer', background: '#fff' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontFamily: 'monospace', color: C.navy }}>{s.slip_no}</strong>
        <span style={{ fontSize: 11, fontWeight: 700, color: col, background: bg, borderRadius: 10, padding: '2px 9px' }}>{lbl}</span>
        {showWho && <span style={{ fontSize: 12 }}><strong>{s.raised_by_name}</strong> <span style={{ color: C.muted }}>· {s.department || s.raised_by_role}</span></span>}
        {s.gpo_no && <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.red }}>GPO {s.gpo_no}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: C.muted }}>
          {fmtD(s.created_at)}{s.waiting_days != null && s.status === 'pending' ? ` · waiting ${s.waiting_days}d` : ''}
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: 13, color: C.text, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.challenge}</div>
    </div>
  );
}

// ─── ONE SLIP — the printable form, plus the response ───────────────────────
function SlipView({ slip, onBack, canRespond }) {
  const [s, setS] = useState(slip);
  const [resp, setResp] = useState({ response: '', chosen_solution: '', status: 'resolved' });
  const [err, setErr] = useState('');
  const reload = async () => { const r = await api.get('/help-slips/' + s.id).catch(() => null); if (r) setS(r.data.data); };

  const send = async () => {
    setErr('');
    if (!resp.response.trim()) return setErr('Write what was decided.');
    if (!resp.chosen_solution) return setErr('Say which solution was taken — or “None of the three”.');
    try { await api.post(`/help-slips/${s.id}/respond`, resp); await reload(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save the response'); }
  };
  const withdraw = async () => {
    const reason = askReason('Withdraw this slip?\n\nReason (required):');
    if (reason == null) return;
    try { await api.post(`/help-slips/${s.id}/withdraw`, { reason: reason.trim() }); await reload(); }
    catch (e) { window.alert(e.response?.data?.error || 'Could not withdraw'); }
  };

  const cell = { border: '1.5px solid #000', padding: '8px 11px' };

  return (
    <div>
      <div className="hs-noprint" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ ...btn, background: '#F3F4F6', color: C.text }}>← Back</button>
        <button onClick={() => window.print()} style={btnP}>🖨 Print this slip</button>
        {s.status === 'pending' && !canRespond && <button onClick={withdraw} style={{ ...btn, background: 'transparent', color: C.red, border: '1px solid #f0cccc' }}>Withdraw</button>}
      </div>

      <div className="hs-print">
        <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>HELP SLIP</div>
        <div className="hs-box" style={{ border: '1.5px solid #000' }}>
          <div className="hs-head" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            {[['Name', s.person_name || s.raised_by_name], ['Ph. No.', s.phone || ''], ['Department', s.department || '']].map(([lbl, v], i) => (
              <div key={lbl} style={{ ...cell, border: 'none', borderRight: i < 2 ? '1.5px solid #000' : 'none' }}>
                <div style={{ fontSize: 8.5, letterSpacing: 1, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>{lbl}</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{v || ' '}</div>
              </div>
            ))}
          </div>
          <SlipField label={L.challenge} min={70}>{s.challenge}</SlipField>
          <SlipField label={L.s1}>{s.solution_1}</SlipField>
          <SlipField label={L.s2}>{s.solution_2}</SlipField>
          <SlipField label={L.s3}>{s.solution_3}</SlipField>
          <div style={{ borderTop: '1.5px solid #000', padding: '6px 11px', fontSize: 10.5, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span><strong>Slip</strong> {s.slip_no}</span>
            <span><strong>Raised</strong> {fmtD(s.created_at)}</span>
            {s.gpo_no && <span><strong>GPO</strong> {s.gpo_no}{s.style_no ? ` · ${s.style_no}` : ''}</span>}
          </div>
        </div>

        {/* The response prints with the slip — the record and the decision on one page. */}
        {s.responded_at && (
          <div className="hs-box" style={{ border: '1.5px solid #000', borderTop: 'none', marginTop: -1 }}>
            <div style={{ padding: '5px 11px', fontSize: 10.5, fontWeight: 700, borderBottom: '1px solid #999', background: '#F7F6F3' }}>
              MD’S RESPONSE
            </div>
            <div style={{ padding: 11, fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{s.response}</div>
            <div style={{ padding: '6px 11px', fontSize: 10.5, borderTop: '1px solid #ddd', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <span><strong>Solution taken:</strong> {CHOSEN_LABEL[s.chosen_solution] || '—'}</span>
              <span><strong>By</strong> {s.responded_by_name || '—'}</span>
              <span><strong>On</strong> {fmtD(s.responded_at)}</span>
            </div>
          </div>
        )}
        {s.status === 'withdrawn' && (
          <div className="hs-box" style={{ border: '1.5px solid #000', borderTop: 'none', marginTop: -1, padding: 11, fontSize: 12.5 }}>
            <strong>Withdrawn</strong> {fmtD(s.withdrawn_at)} — {s.withdraw_reason}
          </div>
        )}
      </div>

      {/* RULING 4 — the response form, admin only, never printed. */}
      {canRespond && s.status === 'pending' && (
        <div className="hs-noprint" style={{ marginTop: 16, border: '1px solid ' + C.border, borderRadius: 9, padding: 14, background: '#fff' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Your response</div>
          <textarea style={{ ...input, minHeight: 80, resize: 'vertical' }} placeholder="What was decided…"
            value={resp.response} onChange={e => setResp({ ...resp, response: e.target.value })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: C.muted }}>Solution taken:</span>
            {[['first', 'Best'], ['second', 'Second'], ['third', 'Third'], ['none', 'None of the three']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setResp({ ...resp, chosen_solution: k })}
                style={{ ...btn, padding: '5px 12px', fontSize: 12,
                  background: resp.chosen_solution === k ? C.navy : '#F3F4F6',
                  color: resp.chosen_solution === k ? '#fff' : '#555' }}>{l}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: C.muted }}>Mark as:</span>
            {[['resolved', 'Resolved'], ['met', 'Met — still open']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setResp({ ...resp, status: k })}
                style={{ ...btn, padding: '5px 12px', fontSize: 12,
                  background: resp.status === k ? C.green : '#F3F4F6',
                  color: resp.status === k ? '#fff' : '#555' }}>{l}</button>
            ))}
            <button onClick={send} style={{ ...btnP, marginLeft: 'auto' }}>Save response</button>
          </div>
          {err && <div style={{ color: C.red, marginTop: 8, fontSize: 12.5 }}>{err}</div>}
        </div>
      )}
    </div>
  );
}
